// Type definitions for the digital menu system

// Product extras (option groups)
export interface ProductExtra {
  id: string;
  name: string;
  description?: string;
  maxSelections: number; // Maximum number of options that can be selected
  minQuantity?: number; // Minimum number of options that must be selected
  required?: boolean; // Whether at least one option must be selected
  options: ProductExtraOption[];
  sequence?: number;
}

export interface ProductExtraOption {
  id: string;
  name: string;
  description?: string;
  price?: number; // Additional price for this option
  imageUrl?: string;
  available: boolean;
  status?: string;
  sequence?: number;
}

// Cashback
export enum CashbackType {
  PERCENTAGE = 'PERCENTAGE',
  FIXED = 'FIXED'
}

export enum CashbackStatus {
  PENDING = 'PENDING',
  AVAILABLE = 'AVAILABLE',
  USED = 'USED',
  EXPIRED = 'EXPIRED',
  CANCELED = 'CANCELED',
  REVERSED = 'REVERSED'
}

export enum CashbackTransactionType {
  EARN = 'EARN',
  USE = 'USE',
  EXPIRE = 'EXPIRE',
  CANCEL = 'CANCEL',
  REVERSAL = 'REVERSAL',
  ADJUSTMENT = 'ADJUSTMENT'
}

export interface CashbackConfig {
  enabled: boolean;
  type: CashbackType;
  value: number;
  minOrderValue: number;
  maxCashbackPerOrder?: number;
  expirationDays: number;
  allowWithCoupon: boolean;
  allowPartialUse: boolean;
  maxUsagePercentagePerOrder?: number;
}

export interface CustomerCashbackBalance {
  availableBalance: number;
  pendingBalance: number;
  expiredBalance: number;
  totalEarned: number;
  totalUsed: number;
}

export interface WalletBalanceDto {
  availableBalance: number;
  pendingCashback: number;
}

export interface WalletTransactionDto {
  type: 'CASHBACK_EARN' | 'CASHBACK_USE' | 'CASHBACK_REVERSAL' | 'CASHBACK_EXPIRED' | 'CASHBACK_RELEASED' | 'PAYMENT';
  status: 'PENDING' | 'COMPLETED' | 'CANCELED';
  direction?: 'IN' | 'OUT';
  amount: number;
  orderId?: string;
  orderExternalId?: string;
  paymentMethods?: PaymentMethod[];
  description?: string;
  createdAt: string;
}

export interface PageWalletTransactionDto {
  totalPages: number;
  totalElements: number;
  size: number;
  content: WalletTransactionDto[];
  number: number;
  numberOfElements: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface CustomerCashbackTransaction {
  id: string;
  customerId: string;
  orderId?: string;
  amount: number;
  type: CashbackTransactionType;
  status: CashbackStatus;
  description: string;
  createdAt: string;
  expiresAt?: string;
}

// Shipping calculation
export interface ShippingInfo {
  address: Address;
  cost: number;
  estimatedTime: string; // e.g., "30-45 min"
}

// Coupon/discount
export interface Coupon {
  code: string;
  discountValue?: number; // Absolute discount value
  discountPercentage?: number; // Percentage discount (0-100)
  minOrderValue?: number; // Minimum order value to apply the coupon
  expiryDate?: string; // ISO date string
  isValid: boolean;
  discountAmount?: number; // Calculated discount amount from API
}

export interface CouponValidationRequestDto {
  code: string;
  customerId: string;
  items: CouponValidationItemDto[];
}

export interface CouponValidationItemDto {
  productId: string;
  categoryId: string;
  price: number;
  quantity: number;
}

export interface CouponValidationResponseDto {
  valid: boolean;
  code: string;
  discountAmount: number;
  eligibleAmount: number;
  message?: string;
}

// Restaurant information
export interface Restaurant {
  id: string;
  name: string;
  slug?: string;
  enabled: boolean;
  isOpen: boolean;
  description?: string;
  logo?: string;
  coverImage?: string;
  address?: Address;
  contact?: Contact;
  deliveryInfo?: DeliveryInfoDto;
  openingHours?: OpeningHours[];
  theme?: MerchantThemeDto;
  cashbackConfig?: CashbackConfig;
  paymentMethods?: PaymentMethodDto[];
  deliveryOptions?: MerchantDeliveryOptionsDto;
  restaurantCategories?: RestaurantCategoryDto[];
  tags?: MerchantTagDto[];
  categories?: CategoryDto[];
  stockControlEnabled: boolean;
  schedulingEnabled: boolean;
  autoAcceptOrders?: boolean;
  hasConnectedPos?: boolean;
  pauses?: MerchantPauseDto[];
  temporaryClosures?: MerchantTemporaryClosureDto[];
  banners?: BannerDto[];
  rating?: number;
  reviewCount?: number;
  enabledFeatures?: string[];
}

// Address information
export interface Address {
  street: string;
  number: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
}

// Contact information
export interface Contact {
  phone: string;
  email?: string;
  website?: string;
  socialMedia?: SocialMedia;
}

// Social media links
export interface SocialMedia {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  whatsapp?: string;
}

// Opening hours
export interface OpeningHours {
  dayOfWeek: number; // 0-6 (Sunday-Saturday)
  timeSlots: TimeSlot[];
}

// Time slot for opening hours
export interface TimeSlot {
  open: string; // HH:MM format
  close: string; // HH:MM format
}

// Menu category
export interface Category {
  id: string;
  name: string;
  description?: string;
  image?: string;
  items: MenuItem[];
  promos?: CatalogPromotionDto[];
  template?: 'DEFAULT' | 'PIZZA';
  sequence: number;
}

// Review
export interface Review {
  id: string;
  userName: string;
  rating: number; // 1-5
  comment?: string;
  date: string;
}

// Dietary preferences
export enum DietaryPreference {
  VEGETARIAN = 'VEGETARIAN',
  VEGAN = 'VEGAN',
  GLUTEN_FREE = 'GLUTEN_FREE',
  LACTOSE_FREE = 'LACTOSE_FREE',
  SUGAR_FREE = 'SUGAR_FREE',
  LOW_CARB = 'LOW_CARB',
  KETO = 'KETO',
  PALEO = 'PALEO'
}

// Menu item
export interface MenuItem {
  id: string;
  categoryId?: string;
  name: string;
  description?: string;
  price: number;
  originalPrice?: number; // Previous price before discount
  image?: string;
  options?: MenuItemOption[];
  extras?: ProductExtra[]; // Product configuration options (e.g., cooking preferences, toppings)
  ingredients?: string[];
  allergens?: string[];
  nutritionalInfo?: ProductNutritionalInfoDto;
  available: boolean;
  featured?: boolean;
  isNew?: boolean; // Flag to indicate if the item is new
  rating?: number;
  ratingCount?: number;
  reviews?: Review[];
  dietaryPreferences?: DietaryPreference[];
  quantity: number;
  availability?: ProductAvailabilityDto;
  inventory?: ProductInventoryDto;
  dietaryRestriction?: string;
  productType?: string;
  status?: string;
  pdvCode?: string;
  sku?: string;
  serving?: string;
  comercial?: ProductComercialDto;
  drinkInfo?: ProductDrinkInfoDto;
  operational?: ProductOperationalDto;
  fiscal?: ProductFiscalDto;
  subProducts?: ComboSubProductDto[];
  sequence?: number;
}

export interface ComboSubProductDto {
  id?: string;
  productId?: string;
  name: string;
  quantity: number;
  description?: string;
  imageUrl?: string;
  price?: number;
  productType?: string;
}

// Menu item option (e.g., size, extras)
export interface MenuItemOption {
  id: string;
  name: string;
  description?: string;
  price: number;
  available: boolean;
  imageUrl?: string;
}

// Nutritional information
export interface NutritionalInfo {
  calories?: number;
  proteins?: number;
  carbohydrates?: number;
  fats?: number;
  sodium?: number;
}

// Order
export interface Order {
  id: string;
  merchantId: string;
  orderDisplayId?: string;
  items: OrderItem[];
  customer?: Customer;
  status: OrderStatus;
  createdAt: string;
  updatedAt: string;
  subtotal?: number;
  total: number;
  deliveryFee?: number;
  discount?: number;
  couponCode?: string;
  paymentMethod?: PaymentMethod;
  payments?: OrderPaymentDto[];
  deliveryMethod?: DeliveryMethod;
  deliveryAddress?: AddressDto;
  estimatedDeliveryTime?: string;
  deliveryConfirmationCode?: string;
  cashbackEarnedAmount?: number;
  cashbackUsedAmount?: number;
  cashbackStatus?: CashbackStatus;
  cancellationReason?: string;
  cancellationReasonDescription?: string;
  reviewInvitation?: OrderReviewInvitationDto;
}

export interface OrderReviewInvitationDto {
  token?: string;
  status: string;
  expiresAt: string;
  alreadyReviewed: boolean;
  expired: boolean;
}

export interface PageOrderDto {
  totalPages: number;
  totalElements: number;
  size: number;
  content: Order[];
  number: number;
  numberOfElements: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

// Order item
export interface OrderItem {
  menuItemId: string;
  categoryId?: string;
  name: string;
  quantity: number;
  price: number;
  imageUrl?: string;
  options?: MenuItemOption[];
  notes?: string;
  available?: boolean;
  status?: string;
  stockQuantity?: number;
  inventory?: ProductInventoryDto;
}

// Customer information
export interface Customer {
  name: string;
  phone: string;
  email?: string;
  address?: Address;
  availableCashbackBalance?: number;
  pendingCashbackBalance?: number;
  expiredCashbackBalance?: number;
}

// Order status
export enum OrderStatus {
  PROCESSING = 'PROCESSING',
  CREATED = 'CREATED',
  RECEIVED = 'RECEIVED',
  CHECK_AVAILABILITY = 'CHECK_AVAILABILITY',
  PROCESS_PAYMENT = 'PROCESS_PAYMENT',
  PAYMENT_CONFIRMED = 'PAYMENT_CONFIRMED',
  PAYMENT_FAILED = 'PAYMENT_FAILED',
  CONFIRMED = 'CONFIRMED',
  ACCEPTED = 'ACCEPTED',
  READY_FOR_TAKEAWAY = 'READY_FOR_TAKEAWAY',
  READY_FOR_DELIVERY = 'READY_FOR_DELIVERY',
  OUT_FOR_DELIVERY = 'OUT_FOR_DELIVERY',
  DELIVERED = 'DELIVERED',
  CANCELLATION_REQUESTED = 'CANCELLATION_REQUESTED',
  CANCELLED = 'CANCELLED',
  CANCELLED_BY_TIMEOUT = 'CANCELLED_BY_TIMEOUT',
  CANCELLED_BY_MERCHANT = 'CANCELLED_BY_MERCHANT',
  CANCELLED_BY_CUSTOMER = 'CANCELLED_BY_CUSTOMER',
  CANCELLED_BY_CHECK_AVAILABILITY = 'CANCELLED_BY_CHECK_AVAILABILITY',
  REFUND_INITIATED = 'REFUND_INITIATED',
  REFUND_CONFIRMED = 'REFUND_CONFIRMED',
  SCHEDULED = 'SCHEDULED',
  ERROR = 'ERROR'
}

export interface CustomerDto {
  id: string;
  firstName: string;
  lastName: string;
  cpf: string;
  email: string;
  phone: string;
  gender: 'MALE' | 'FEMALE';
  birthDate: string;
  image?: string;
  availableCashbackBalance?: number;
  pendingCashbackBalance?: number;
  expiredCashbackBalance?: number;
}

export interface CustomerLoginRequestDto {
  cpfOrPhone: string;
  password: string;
  deviceToken?: string;
}

export interface CreateOrderCustomerRequestDto {
  id?: string;
  name: string;
  phone: string;
  email?: string;
  document?: string;
  imageUrl?: string;
}

export interface OrderPaymentDto {
  method: string;
  amount: number;
  changeFor?: number;
}

export interface OrderItemModifierDto {
  id: string;
  name: string;
  price: number;
  quantity: number;
}

export interface CreateOrderItemDto {
  id: string;
  name: string;
  price: number;
  quantity: number;
  type: 'PRODUCT' | 'COMBO';
  modifiers: OrderItemModifierDto[];
  notes?: string;
}

export interface CreateOrderRequestDto {
  merchantId: string;
  payments: OrderPaymentDto[];
  originChannel: 'MENU_WEB' | 'APP' | 'PDV' | 'MARKETPLACE' | 'IFOOD';
  type: 'TAKEAWAY' | 'DELIVERY' | 'PARTNER_DELIVERY';
  customer: CreateOrderCustomerRequestDto;
  couponCode?: string | null;
  deliveryAddress: AddressDto;
  items: CreateOrderItemDto[];
  scheduledTo?: string;
  cartCode?: string;
}

export interface CheckoutValidationResponseDto {
  isValid: boolean;
  issues: string[];
  errorCodes?: string[];
}

export interface CustomerLoginResponseDto {
  user: CustomerDto;
  accessToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
}

export interface CustomerRegisterRequestDto {
  name: string;
  cpf: string;
  email: string;
  password: string;
  phone: string;
  gender: 'MALE' | 'FEMALE';
  birthDate: string;
}

export type SalesRecoverySource = 'CART' | 'ABANDONED_CART' | 'INACTIVE_CUSTOMER' | 'CAMPAIGN';
export type SalesRecoveryStatus = 'IN_PROGRESS' | 'POSSIBLE_ABANDONED' | 'ABANDONED' | 'PENDING' | 'SENT' | 'CONVERTED' | 'EXPIRED' | 'NOT_ACTIONABLE';
export type SalesRecoveryCaptureEvent = 'CART_UPDATED' | 'PAGE_CLOSED';

export interface SalesRecoveryItemDto {
  productId: string;
  name: string;
  quantity: number;
  price: number;
  total?: number;
}

export interface SalesRecoveryOpportunityCaptureRequestDto {
  merchantId: string;
  cartCode: string;
  sessionId?: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  pushToken?: string;
  pushOptIn?: boolean;
  source?: SalesRecoverySource;
  eventType?: SalesRecoveryCaptureEvent;
  items: SalesRecoveryItemDto[];
  subtotal: number;
  total: number;
  couponCode?: string | null;
}

export interface SalesRecoveryOpportunityDto {
  id: string;
  merchantId: string;
  source: SalesRecoverySource;
  status: SalesRecoveryStatus;
  cartCode: string;
  sessionId?: string;
  customerId?: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  hasPushToken: boolean;
  pushOptIn: boolean;
  contactable: boolean;
  itemCount: number;
  subtotal: number;
  total: number;
  couponCode?: string;
  lastActivityAt: string;
  expiresAt?: string;
  convertedAt?: string;
  convertedOrderId?: string;
  createdAt: string;
}

export interface UserInfo {
  cpf: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface CustomerRegisterResponseDto {
  message: string;
  user: UserInfo;
}

export interface CustomerRefreshRequestDto {
  refreshToken: string;
}

export interface ImageDto {
  base64: string;
  contentType: string;
}

// Alinhado ao contrato do backend (UpdateUserProfileRequestDto): o CPF NÃO é
// editável e não deve ser enviado — o backend rejeita campos desconhecidos.
export interface UpdateUserProfileRequestDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  gender?: 'MALE' | 'FEMALE';
  birthDate?: string;
  image?: ImageDto;
}

// API DTOs (based on openapi.yaml)
export interface AddressDto {
  id?: string;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  zipCode: string;
  complement?: string;
  latitude?: number;
  longitude?: number;
  nickname?: string;
}

export interface AddressSuggestionDto {
  placeId: string;
  description: string;
}

export interface ContactInfoDto {
  phone?: string;
  email?: string;
  website?: string;
}

export interface DeliveryInfoDto {
  fee: number;
  distance: number;
  minTime: number;
  maxTime: number;
}

export interface ProductModifierOptionDto {
  id: string;
  name: string;
  description?: string;
  price: number;
  available: boolean;
  imageUrl?: string;
  status?: string;
  sequence?: number;
}

export interface ProductModifierDto {
  id: string;
  name: string;
  required: boolean;
  minQuantity: number;
  maxQuantity: number;
  options: ProductModifierOptionDto[];
  description?: string;
  sequence?: number;
}

export interface ProductAvailabilityScheduleDto {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

export interface ProductAvailabilityDto {
  availableDelivery: boolean;
  availableTakeaway: boolean;
  availableDineIn: boolean;
  availabilityType: string;
  schedules: ProductAvailabilityScheduleDto[];
}

export interface NutritionalTableDto {
  calories?: number;
  proteins?: number;
  carbohydrates?: number;
  fats?: number;
}

export interface ProductNutritionalInfoDto {
  allergens: string[];
  labels: string[];
  nutritionalTable?: NutritionalTableDto;
}

export interface ProductInventoryDto {
  stockControl: boolean;
  stockQuantity: number;
  stockUnit?: string;
  blockWithoutStock: boolean;
}

export interface ProductComercialDto {
  basePrice?: number;
  promotionalPrice?: number;
  promotionActive?: boolean;
  promotionStart?: string;
  promotionEnd?: string;
  discountPercentage?: number;
  highlight?: boolean;
}

export interface ProductDrinkInfoDto {
  volumeMl?: number;
  alcoholic: boolean;
  requiresAgeVerification: boolean;
  coldByDefault: boolean;
  allowsNoIce: boolean;
  returnable?: boolean;
}

export interface ProductOperationalDto {
  preparationTimeMinutes?: number;
  productionCenterId?: string;
  destinationPrinter?: string;
  priority: number;
  limitPerOrder?: number;
  limitPerCustomerDay?: number;
}

export interface ProductFiscalDto {
  ncm?: string;
  cfop?: string;
  cst?: string;
  cest?: string;
  taxRate?: number;
  productNature?: string;
}

export interface ProductDto {
  id: string;
  categoryId?: string;
  name: string;
  price: number;
  available: boolean;
  status?: string;
  description?: string;
  imageUrl?: string;
  quantity: number;
  modifiers: ProductModifierDto[];
  dietaryRestriction?: string;
  availability?: ProductAvailabilityDto;
  inventory?: ProductInventoryDto;
  nutritionalInfo?: ProductNutritionalInfoDto;
  productType?: string;
  pdvCode?: string;
  sku?: string;
  serving?: string;
  comercial?: ProductComercialDto;
  drinkInfo?: ProductDrinkInfoDto;
  operational?: ProductOperationalDto;
  fiscal?: ProductFiscalDto;
  subProducts?: ComboSubProductDto[];
  sequence?: number;
}

export interface CategoryDto {
  id: string;
  name: string;
  imageUrl?: string;
  description?: string;
  products: ProductDto[];
  promos: CatalogPromotionDto[];
  template?: 'DEFAULT' | 'PIZZA';
  sequence: number;
}

export interface MerchantThemeDto {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
  lightText: string;
  success: string;
  error: string;
}

export interface PaymentMethodDto {
  id: string;
  name: string;
  onlinePayment: boolean;
  accepted: boolean;
}

export interface BannerDto {
  id: string;
  title: string;
  description?: string;
  imageUrl: string;
  startDate?: string | null;
  endDate?: string | null;
}

export interface MerchantBannerDto {
  id: string;
  title: string;
  description?: string;
  imageUrl: string;
  startDate?: string | null;
  endDate?: string | null;
}

export interface MerchantDto {
  id: string;
  name: string;
  slug: string;
  enabled: boolean;
  isOpen: boolean;
  categories: CategoryDto[];
  deliveryInfo?: DeliveryInfoDto;
  contactInfo?: ContactInfoDto;
  address: AddressDto;
  logoUrl?: string;
  coverUrl?: string;
  description?: string;
  rating?: number;
  reviewCount?: number;
  cashbackConfig?: CashbackConfig;
  theme?: MerchantThemeDto;
  paymentMethods: PaymentMethodDto[];
  openingHours: MerchantOpeningHourDto[];
  deliveryOptions: MerchantDeliveryOptionsDto;
  deliveryRules: MerchantDeliveryRadiusConfigDto[];
  restaurantCategories: RestaurantCategoryDto[];
  stockControlEnabled: boolean;
  schedulingEnabled: boolean;
  autoAcceptOrders: boolean;
  hasConnectedPos: boolean;
  pauses: MerchantPauseDto[];
  temporaryClosures: MerchantTemporaryClosureDto[];
  banners?: BannerDto[];
  tags: MerchantTagDto[];
  enabledFeatures?: string[];
}

export interface MerchantPauseDto {
  id: number;
  startAt: string;
  endAt: string;
  reason?: string;
}

export interface MerchantTemporaryClosureDto {
  id: number;
  startAt: string;
  endAt: string;
  reason?: string;
}

export interface RestaurantCategoryDto {
  id: string;
  name: string;
  imageUrl?: string;
}

export interface MerchantTagDto {
  id: string;
  name: string;
  imageUrl?: string;
  description?: string;
}

export type CatalogPromotionType =
  | 'COMBO_FIXED_PRICE'
  | 'QUANTITY_PERCENTAGE_DISCOUNT'
  | 'QUANTITY_FIXED_DISCOUNT'
  | 'BUY_X_GET_Y_PERCENTAGE'
  | 'BUY_X_GET_Y_FIXED'
  | 'BUY_X_GET_Y_FREE';

export type CatalogPromotionDiscountType = 'PERCENTAGE' | 'FIXED_AMOUNT' | 'FREE' | 'FIXED_PRICE';

export type CatalogPromotionTargetType = 'PRODUCT' | 'CATEGORY';

export interface CatalogPromotionCategoryDto {
  id: string;
  name: string;
}

export interface CatalogPromotionScheduleDto {
  weekday: string;
  startTime: string;
  endTime: string;
}

export interface CatalogPromotionConditionDto {
  id?: string;
  targetType: CatalogPromotionTargetType;
  productId?: string;
  categoryId?: string;
  requiredQuantity: number;
}

export interface CatalogPromotionBenefitDto {
  targetType: CatalogPromotionTargetType;
  productId?: string;
  categoryId?: string;
  benefitQuantity: number;
  discountType: CatalogPromotionDiscountType;
  discountValue: number;
  fixedComboPrice?: number;
}

export interface CatalogPromotionDto {
  id: string;
  merchantId: string;
  name: string;
  description?: string;
  type: CatalogPromotionType;
  active: boolean;
  priority: number;
  startDate?: string;
  endDate?: string;
  minimumOrderValue?: number;
  maximumDiscountValue?: number;
  allowWithCoupon: boolean;
  allowWithCashback: boolean;
  allowWithOtherPromotions: boolean;
  usageLimitTotal?: number;
  usageLimitPerCustomer?: number;
  imageUrl?: string;
  bannerUrl?: string;
  showInMenu: boolean;
  categoryIds: string[];
  categories: CatalogPromotionCategoryDto[];
  schedules: CatalogPromotionScheduleDto[];
  conditions: CatalogPromotionConditionDto[];
  benefits: CatalogPromotionBenefitDto[];
}


export interface MerchantOpeningHourDto {
  dayOfWeek: number;
  timeSlots: OpeningHourSlotDto[];
}

export interface OpeningHourSlotDto {
  open: string;
  close: string;
}

export interface MerchantDeliveryOptionsDto {
  delivery: boolean;
  takeaway: boolean;
  dineIn: boolean;
  takeawayTime?: string;
  minimumOrderValue: number;
  schedulingEnabled: boolean;
  deliveryRadiusConfig: MerchantDeliveryRadiusConfigDto[];
}

export interface MerchantDeliveryRadiusConfigDto {
  radiusKm: number;
  fee: number;
  estimatedTime: string;
}

export interface ReviewDto {
  id: string;
  merchantId: string;
  orderExternalId?: string;
  customerExternalId?: string;
  customerName: string;
  rating: number;
  comment?: string;
  merchantResponse?: string;
  createdAt: string;
}

export type ReviewInvitationStatus = 'PENDING' | 'REVIEWED' | 'EXPIRED' | 'CANCELLED';

export interface ReviewInvitationPublicDto {
  merchantId: string;
  merchantName: string;
  orderExternalId: string;
  orderDisplayId?: string;
  customerName?: string;
  status: ReviewInvitationStatus;
  expiresAt: string;
  alreadyReviewed: boolean;
  expired: boolean;
}

export interface SubmitOrderReviewRequestDto {
  token: string;
  rating: number;
  comment?: string;
  customerName?: string;
}

export interface OrderItemDto {
  id: string;
  productId: string;
  name: string;
  price: number;
  quantity: number;
  total: number;
  modifiers: OrderItemModifierDto[];
  notes?: string;
}

export interface AsyncOrderResponseDto {
  id: string;
  status: 'PROCESSING' | 'CREATED' | string;
  message: string;
}

export interface OrderDto {
  id: string;
  merchantId: string;
  status: string;
  items: OrderItemDto[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  couponCode?: string;
  discount?: number;
  payments?: OrderPaymentDto[];
  paymentMethod?: string;
  deliveryAddress: AddressDto;
  orderDisplayId: string;
  createdAt: string;
  estimatedDeliveryTime?: string;
  deliveryConfirmationCode?: string;
  cashbackEarnedAmount?: number;
  cashbackUsedAmount?: number;
  cashbackStatus?: CashbackStatus;
  cancellationReason?: string;
  cancellationReasonDescription?: string;
  reviewInvitation?: OrderReviewInvitationDto;
}

export interface MerchantDomainsResponseDto {
  merchantId: string;
}

// Payment method
export enum PaymentMethod {
  CREDIT_CARD_ONLINE = 'CREDIT_CARD_ONLINE',
  CREDIT_CARD_OFFLINE = 'CREDIT_CARD_OFFLINE',
  DEBIT_CARD_ONLINE = 'DEBIT_CARD_ONLINE',
  DEBIT_CARD_OFFLINE = 'DEBIT_CARD_OFFLINE',
  PIX_ONLINE = 'PIX_ONLINE',
  PIX_OFFLINE = 'PIX_OFFLINE',
  GOOGLE_PAY = 'GOOGLE_PAY',
  APPLE_PAY = 'APPLE_PAY',
  PAYPAL = 'PAYPAL',
  MEAL_VOUCHER_ONLINE = 'MEAL_VOUCHER_ONLINE',
  MEAL_VOUCHER_OFFLINE = 'MEAL_VOUCHER_OFFLINE',
  BANK_TRANSFER = 'BANK_TRANSFER',
  STORE_CREDIT = 'STORE_CREDIT',
  CRYPTOCURRENCY = 'CRYPTOCURRENCY',
  CASH = 'CASH',
  COUPON = 'COUPON'
}

export const PAYMENT_METHOD_NAMES: Record<string, string> = {
  CREDIT_CARD_ONLINE: 'Cartão de Crédito (Online)',
  CREDIT_CARD_OFFLINE: 'Cartão de Crédito (Maquininha)',
  DEBIT_CARD_ONLINE: 'Cartão de Débito (Online)',
  DEBIT_CARD_OFFLINE: 'Cartão de Débito (Maquininha)',
  PIX_ONLINE: 'Pix online (Processado pela Léo Delivery)',
  PIX_OFFLINE: 'Pix',
  GOOGLE_PAY: 'Google Pay',
  APPLE_PAY: 'Apple Pay',
  PAYPAL: 'PayPal',
  MEAL_VOUCHER_ONLINE: 'Vale Refeição (Online)',
  MEAL_VOUCHER_OFFLINE: 'Vale Refeição (Maquininha)',
  BANK_TRANSFER: 'Transferência Bancária',
  STORE_CREDIT: 'Crédito na Loja',
  CRYPTOCURRENCY: 'Criptomoeda',
  CASH: 'Dinheiro',
  COUPON: 'Cupom',
  CASHBACK: 'Cashback'
};

export const PAYMENT_METHOD_DESCRIPTIONS: Record<string, string> = {
  CREDIT_CARD_ONLINE: 'Pagamento processado online via cartão de crédito',
  CREDIT_CARD_OFFLINE: 'Pagamento processado na maquininha na entrega',
  DEBIT_CARD_ONLINE: 'Pagamento processado online via cartão de débito',
  DEBIT_CARD_OFFLINE: 'Pagamento processado na maquininha na entrega',
  PIX_ONLINE: 'Pagamento via Pix online',
  PIX_OFFLINE: 'Pagamento via Pix',
  GOOGLE_PAY: 'Carteira digital Google Pay',
  APPLE_PAY: 'Carteira digital Apple Pay',
  PAYPAL: 'Sistema de pagamento PayPal',
  MEAL_VOUCHER_ONLINE: 'Pagamento online via vale refeição/alimentação',
  MEAL_VOUCHER_OFFLINE: 'Pagamento via maquininha com vale refeição/alimentação',
  BANK_TRANSFER: 'Transferência bancária direta',
  STORE_CREDIT: 'Saldo de crédito na loja do cliente',
  CRYPTOCURRENCY: 'Pagamento via criptomoedas',
  CASH: 'Pagamento em dinheiro',
  COUPON: 'Desconto via cupom',
  CASHBACK: 'Desconto via cashback'
};

export const OFFLINE_PAYMENT_METHODS = [
  { id: PaymentMethod.CASH, name: PAYMENT_METHOD_NAMES.CASH, icon: 'fa-money-bill-wave' },
  { id: PaymentMethod.CREDIT_CARD_OFFLINE, name: PAYMENT_METHOD_NAMES.CREDIT_CARD_OFFLINE, icon: 'fa-credit-card' },
  { id: PaymentMethod.DEBIT_CARD_OFFLINE, name: PAYMENT_METHOD_NAMES.DEBIT_CARD_OFFLINE, icon: 'fa-credit-card' },
  { id: PaymentMethod.MEAL_VOUCHER_OFFLINE, name: PAYMENT_METHOD_NAMES.MEAL_VOUCHER_OFFLINE, icon: 'fa-utensils' }
];

// Delivery method
export enum DeliveryMethod {
  DELIVERY = 'DELIVERY',
  TAKEAWAY = 'TAKEAWAY',
  DINE_IN = 'DINE_IN'
}
